/**
 * Autosave status pill + version-history button shared by the persisted office
 * document editors (`/sheets/$id`, `/slides/$id`). Mirrors the note editor's
 * header affordances (saving → error → saved, plus a History button).
 */
import { Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertCircle, Check, History, Loader2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";

interface DocumentToolbarStatusProps {
  isSaving: () => boolean;
  saveError: () => boolean;
  lastSavedAt: () => number | null;
  onOpenVersions: () => void;
}

export function DocumentToolbarStatus(props: DocumentToolbarStatusProps) {
  const { t } = useI18n();
  return (
    <div class="flex items-center gap-1" data-testid="document-status">
      <Show
        when={props.saveError()}
        fallback={
          <Show
            when={props.isSaving()}
            fallback={
              <Show when={props.lastSavedAt()}>
                <span
                  class="flex shrink-0 items-center gap-1 text-xs font-medium text-success motion-safe:animate-fade-in"
                  aria-live="polite"
                  data-testid="document-saved"
                >
                  <Check class="h-3.5 w-3.5" />
                  <span class="hidden sm:inline">{t(msg`Saved`)}</span>
                </span>
              </Show>
            }
          >
            <span
              class="flex shrink-0 items-center gap-1 text-xs text-muted-foreground/60"
              aria-live="polite"
              data-testid="document-saving"
            >
              <Loader2 class="h-3.5 w-3.5 animate-spin" />
              <span class="hidden sm:inline">{t(msg`Saving…`)}</span>
            </span>
          </Show>
        }
      >
        <span
          class="flex shrink-0 items-center gap-1 text-xs font-medium text-danger"
          aria-live="assertive"
          role="status"
          data-testid="document-save-failed"
        >
          <AlertCircle class="h-3.5 w-3.5" />
          <span class="hidden sm:inline">{t(msg`Save failed`)}</span>
        </span>
      </Show>
      <Button
        variant="ghost"
        size="sm"
        class="shrink-0 gap-1.5"
        data-testid="version-history-open"
        onClick={() => props.onOpenVersions()}
        aria-label={t(msg`Version history`)}
        title={t(msg`Version history`)}
      >
        <History class="h-4 w-4" />
        <span class="hidden sm:inline">{t(msg`History`)}</span>
      </Button>
    </div>
  );
}
